/**
 * ApiKeysCard — sekcja "Klucze API" w panelu Ustawień Agentów AI.
 *
 * BEZPIECZEŃSTWO:
 *  - Klucze są ZAPISYWANE bez wstrząsania ENV ani plikiem .env.
 *  - Backend NIGDY nie zwraca plaintext klucza po zapisaniu.
 *  - Wartość input nigdy nie ląduje w localStorage, sessionStorage, cookie.
 *  - Po zapisie pole input jest czyszczone.
 *  - Pole password (type="password"), autoComplete="off".
 *  - Status pokazuje tylko "Klucz skonfigurowany" / "Brak klucza" + source (env|db).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Save, Trash2, CheckCircle2, AlertTriangle, Eye, EyeOff, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { logger } from "../../utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDERS = [
  { id: "openai", label: "OpenAI API Key", placeholder: "sk-…" },
  { id: "anthropic", label: "Anthropic API Key", placeholder: "sk-ant-…" },
  { id: "gemini", label: "Gemini API Key", placeholder: "AIza… / …" },
  { id: "minimax", label: "MiniMax API Key", placeholder: "…" },
  { id: "openrouter", label: "OpenRouter API Key", placeholder: "sk-or-v1-…" },
];

const SourceBadge = ({ source }) => {
  if (source === "env") {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700">
        <CheckCircle2 className="w-3 h-3" /> Klucz skonfigurowany (ENV)
      </span>
    );
  }
  if (source === "db") {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">
        <CheckCircle2 className="w-3 h-3" /> Klucz skonfigurowany (Secret Store)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-50 border border-gray-200 text-gray-600">
      <AlertTriangle className="w-3 h-3" /> Brak klucza
    </span>
  );
};

const ApiKeyRow = ({ provider, status, onSaved, onDeleted }) => {
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    if (!value || value.length < 10) {
      toast.error("Klucz API musi mieć co najmniej 10 znaków");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/ai/agents/secrets/${provider.id}`,
        { api_key: value },
        { withCredentials: true },
      );
      // CZYŚCIMY pole — nie persistujemy klucza w UI
      setValue("");
      setShow(false);
      toast.success("Klucz zapisany");
      onSaved?.();
    } catch (e) {
      logger.error("save secret error", e);
      let msg = "Błąd zapisu klucza";
      const detail = e.response?.data?.detail;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/ai/agents/secrets/${provider.id}`, { withCredentials: true });
      toast.success("Klucz usunięty z Secret Store");
      setValue("");
      setShow(false);
      onDeleted?.();
    } catch (e) {
      logger.error("delete secret error", e);
      toast.error(e.response?.data?.detail || "Błąd usuwania klucza");
    } finally {
      setDeleting(false);
    }
  };

  const inDb = status?.source === "db";

  return (
    <div className="space-y-1.5" data-testid={`secret-row-${provider.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-xs text-gray-700 font-medium">{provider.label}</Label>
        <SourceBadge source={status?.source || "none"} />
      </div>
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <Input
            type={show ? "text" : "password"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={status?.configured ? "••••••••••••••••••••••• (klucz skonfigurowany)" : provider.placeholder}
            autoComplete="off"
            spellCheck={false}
            className="font-mono text-xs pr-9"
            data-testid={`secret-input-${provider.id}`}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            tabIndex={-1}
            aria-label="Pokaż/ukryj"
            data-testid={`secret-toggle-${provider.id}`}
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving || !value}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid={`secret-save-${provider.id}`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </Button>
        {inDb && (
          <Button
            type="button"
            variant="outline"
            onClick={handleDelete}
            disabled={deleting}
            className="border-red-200 text-red-700 hover:bg-red-50"
            data-testid={`secret-delete-${provider.id}`}
            title="Usuń klucz ze Secret Store"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </Button>
        )}
      </div>
    </div>
  );
};

const ApiKeysCard = ({ providers, onChanged }) => {
  return (
    <Card data-testid="api-keys-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <KeyRound className="w-5 h-5 text-amber-600" />
          Klucze API
        </CardTitle>
        <CardDescription className="text-xs">
          Klucze są szyfrowane i przechowywane w bezpiecznym Secret Store.
          ENV ma priorytet nad Secret Store. Plaintext klucza nigdy nie jest zwracany ani logowany.
          Po zapisie pole jest czyszczone i wartość nie pojawia się ponownie.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {PROVIDERS.map((p) => (
          <ApiKeyRow
            key={p.id}
            provider={p}
            status={providers?.[p.id]}
            onSaved={onChanged}
            onDeleted={onChanged}
          />
        ))}
      </CardContent>
    </Card>
  );
};

export default ApiKeysCard;
